import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../contexts/AuthContext';

interface ContactInfo {
  email: string;
  prenom: string;
  nom: string;
  telephone?: string;
}

// Type pour le retour de la RPC get_contact_info
type ContactInfoRPC = {
  email: string | null;
  telephone: string | null;
  prenom: string;
  nom: string;
};

interface ContactInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  matchedUserId: string; // L'ID de l'utilisateur avec qui on a matché
  isStudent?: boolean; // true si on affiche les infos d'un étudiant, false pour un tuteur
  onRateTutor?: () => void; // callback optionnel pour déclencher la notation
}

export const ContactInfoModal: React.FC<ContactInfoModalProps> = ({ 
  isOpen, 
  onClose, 
  matchedUserId,
  isStudent = false,
  onRateTutor
}) => {
  const { user } = useAuth();
  const [contactInfo, setContactInfo] = useState<ContactInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedPhone, setCopiedPhone] = useState(false);

  useEffect(() => {
    if (isOpen && matchedUserId) {
      // Reset des états de copie quand le modal s'ouvre
      setCopiedEmail(false);
      setCopiedPhone(false);
      
      const loadContactInfo = async () => {
        if (!user) return;
        
        setLoading(true);
        setError(null);

        try {
          // Récupérer l'ID du profil à partir de l'user ID
          // Pour un étudiant, on a directement son profil ID
          // Pour un tuteur, on a son profil ID
          let targetProfilId: string;
          
          if (isStudent) {
            // matchedUserId est l'user_id de l'étudiant, on doit récupérer son profil_id
            const { data: studentProfile, error: studentError } = await supabase
              .from('profils')
              .select('id')
              .eq('user_id', matchedUserId)
              .single();
              
            if (studentError || !studentProfile) {
              setError('Erreur lors de la récupération du profil étudiant.');
              return;
            }
            targetProfilId = studentProfile.id;
          } else {
            // matchedUserId est l'user_id du tuteur, on doit récupérer son profil_id  
            const { data: tutorProfile, error: tutorError } = await supabase
              .from('profils')
              .select('id')
              .eq('user_id', matchedUserId)
              .single();
              
            if (tutorError || !tutorProfile) {
              setError('Erreur lors de la récupération du profil tuteur.');
              return;
            }
            targetProfilId = tutorProfile.id;
          }

          // Utiliser la RPC sécurisée pour récupérer les informations de contact
          const { data: rpcData, error: rpcError } = await supabase
            .rpc('get_contact_info', { target_profil_id: targetProfilId });

          if (rpcError) {
            setError(rpcError.message || 'Accès non autorisé. Aucun match mutuel trouvé.');
            return;
          }

          if (!rpcData || rpcData.length === 0) {
            setError('Aucune information de contact trouvée.');
            return;
          }

          // La RPC retourne un tableau, on prend le premier élément
          const contactData = rpcData[0] as ContactInfoRPC;

          // Debug: console.log('🔍 Données de contact récupérées:', contactData);

          setContactInfo({
            email: contactData.email || '',
            prenom: contactData.prenom || '',
            nom: contactData.nom || '',
            telephone: contactData.telephone || ''
          });

        } catch (err) {
          setError('Une erreur inattendue s\'est produite.');
        } finally {
          setLoading(false);
        }
      };
      
      loadContactInfo();
    }
  }, [isOpen, matchedUserId, user, isStudent]);

  const copyToClipboard = async (text: string, type: 'email' | 'phone') => {
    try {
      await navigator.clipboard.writeText(text);

      // Feedback visuel selon le type
      if (type === 'email') {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      } else {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      }
    } catch (err) {
      // Fallback pour les navigateurs plus anciens
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (type === 'email') {
        setCopiedEmail(true);
        setTimeout(() => setCopiedEmail(false), 2000);
      } else {
        setCopiedPhone(true);
        setTimeout(() => setCopiedPhone(false), 2000);
      }
    }
  };

  const openWhatsApp = (phoneNumber: string) => {
    // Nettoyer le numéro de téléphone (enlever espaces, tirets, etc.)
    const cleanNumber = phoneNumber.replace(/[\s-+()]/g, '');
    // Ouvrir WhatsApp avec le numéro
    window.open(`https://wa.me/${cleanNumber}`, '_blank');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-white bg-opacity-95 flex items-center justify-center z-50 p-4 md:p-8 overflow-y-auto">
      <div className="relative w-full max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl p-6 md:p-10">
        {/* Bouton fermer */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors"
          aria-label="Fermer"
        >
          <i className="fas fa-times text-2xl"></i>
        </button>
        {/* Header */}
        <div className="text-center mb-6">
          <div className="mx-auto h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-handshake text-green-600 text-2xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            🎓 Match académique confirmé !
          </h2>
          <p className="text-gray-600">
            Voici les informations de contact révélées
          </p>
        </div>

        {loading && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#002c46] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des informations...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center">
              <i className="fas fa-exclamation-triangle text-red-600 mr-2"></i>
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          </div>
        )}

        {contactInfo && !loading && !error && (
          <div className="space-y-4 mb-6">
            {/* Nom complet */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom complet
              </label>
              <div className="flex items-center justify-between">
                <span className="text-lg font-semibold text-gray-900">
                  {contactInfo.prenom} {contactInfo.nom}
                </span>
              </div>
            </div>

            {/* Email */}
            <div className="bg-gray-50 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adresse e-mail
              </label>
              <div className="flex items-center justify-between">
                <span className="text-gray-900 break-all">{contactInfo.email}</span>
                <button
                  onClick={() => copyToClipboard(contactInfo.email, 'email')}
                  className={`ml-2 p-2 rounded-lg transition-all duration-300 ${
                    copiedEmail 
                      ? 'text-green-600 bg-green-50 scale-110' 
                      : 'text-blue-600 hover:bg-blue-50'
                  }`}
                  title={copiedEmail ? "Email copié !" : "Copier l'email"}
                >
                  <i className={`fas ${copiedEmail ? 'fa-check' : 'fa-copy'} ${copiedEmail ? 'animate-bounce' : ''}`}></i>
                </button>
              </div>
            </div>

            {/* Téléphone (si disponible) */}
            {contactInfo.telephone && (
              <div className="bg-gray-50 rounded-lg p-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Téléphone
                </label>
                <div className="flex items-center justify-between">
                  <span className="text-gray-900">{contactInfo.telephone}</span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openWhatsApp(contactInfo.telephone!)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Ouvrir WhatsApp"
                    >
                      <i className="fab fa-whatsapp"></i>
                    </button>
                    <button
                      onClick={() => copyToClipboard(contactInfo.telephone!, 'phone')}
                      className={`p-2 rounded-lg transition-all duration-300 ${
                        copiedPhone 
                          ? 'text-green-600 bg-green-50 scale-110' 
                          : 'text-blue-600 hover:bg-blue-50'
                      }`}
                      title={copiedPhone ? "Téléphone copié !" : "Copier le téléphone"}
                    >
                      <i className={`fas ${copiedPhone ? 'fa-check' : 'fa-copy'} ${copiedPhone ? 'animate-bounce' : ''}`}></i>
                    </button>
                  </div>
                </div>
              </div>
            )}



            {/* Actions rapides */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">
                Actions rapides
              </h3>
              <div className="space-y-2">
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="block w-full py-2 px-3 bg-blue-600 text-white text-center rounded-lg hover:bg-blue-700 transition-colors text-sm"
                >
                  <i className="fas fa-envelope mr-2"></i>
                  Envoyer un e-mail
                </a>
                {contactInfo.telephone && (
                  <>
                    <button
                      onClick={() => openWhatsApp(contactInfo.telephone!)}
                      className="block w-full py-2 px-3 bg-green-500 text-white text-center rounded-lg hover:bg-green-600 transition-colors text-sm"
                    >
                      <i className="fab fa-whatsapp mr-2"></i>
                      Contacter sur WhatsApp
                    </button>
                    <a
                      href={`tel:${contactInfo.telephone}`}
                      className="block w-full py-2 px-3 bg-gray-600 text-white text-center rounded-lg hover:bg-gray-700 transition-colors text-sm"
                    >
                      <i className="fas fa-phone mr-2"></i>
                      Appeler
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Actions bas */}
        <div className="flex flex-col md:flex-row gap-3">
          <button
            onClick={onClose}
            className="flex-1 py-3 px-4 bg-heg-blue text-white font-semibold rounded-lg hover:bg-heg-blue-dark transition-colors"
          >
            <i className="fas fa-times mr-2"></i>
            Fermer
          </button>
          {!isStudent && onRateTutor && (
            <button
              onClick={onRateTutor}
              className="flex-1 py-3 px-4 bg-green-600 text-white font-semibold rounded-lg hover:bg-green-700 transition-colors"
            >
              <i className="fas fa-star mr-2"></i>
              Noter ce tuteur
            </button>
          )}
        </div>
      </div>
    </div>
  );
};